import {
  type Interval,
  addDays,
  differenceInCalendarDays,
  eachDayOfInterval,
  startOfWeek,
} from "date-fns";
import { formatInTimeZone, fromZonedTime } from "date-fns-tz";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";

import { APP_LOCAL_KEYS, States } from "@/app/constants/constants";
import { Workspace } from "@/features/workspace/workspace.interfaces";
import { getFortnightFromSession } from "@/utils/datetime/timesheetHelper";
import {
  parseBrowserDateQueryValue,
  sanitizeBrowserQueryParams,
} from "@/utils/form/queryParams";
import { typedLocalStorage } from "@/utils/storage/localStorage";

interface FortnightPickerProps {
  setDateRange: (val: Date[]) => void;
}

const MAX_FORTNIGHT_RANGE_DAYS = 31;

const workspaceDetails = typedLocalStorage.getItem(
  APP_LOCAL_KEYS.WORKSPACE_DATA,
) as Workspace;

const timezone =
  workspaceDetails?.timezone ||
  States.find((state) => state?.name === workspaceDetails?.state)?.timezone ||
  "Australia/NSW"; // fallback timezone

// Helper function to get current date in target timezone
const getCurrentDateInTimezone = (now: Date = new Date()): Date => {
  // Get current time as a string in target timezone
  const nowString = formatInTimeZone(now, timezone, "yyyy-MM-dd");

  // Create a date object that represents midnight of today in the workspace timezone
  // This ensures we're working with date boundaries in the workspace timezone, not device timezone
  const dateInTargetTz = fromZonedTime(`${nowString}T00:00:00`, timezone);

  return dateInTargetTz;
};

// Helper function to create a date in target timezone
const createDateInTimezone = (
  year: number,
  month: number,
  day: number,
): Date => {
  // Create date string in target timezone format
  const dateString = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}T00:00:00`;

  // Convert this string to a Date object that represents the target timezone
  const dateInTargetTz = fromZonedTime(dateString, timezone);

  return dateInTargetTz;
};

// Define the reference date as July 1st in workspace timezone
// Uses previous year's July 1st if current date is before July 1st of current year
const getReferenceDate = (now: Date = new Date()): Date => {
  // Use a single Date instance to avoid race conditions at midnight/year boundaries
  const currentYearString = formatInTimeZone(now, timezone, "yyyy");
  const currentYear = parseInt(currentYearString, 10);

  // Create July 1st of current year
  const july1stCurrentYear = createDateInTimezone(currentYear, 6, 1); // Month is 0-indexed, so 6 = July

  // Get current date in timezone using the same 'now' instance
  const today = getCurrentDateInTimezone(now);

  // If we're before July 1st of current year, use July 1st of previous year
  // This ensures we always have a reference date in the past for correct fortnight calculation
  if (today < july1stCurrentYear) {
    return createDateInTimezone(currentYear - 1, 6, 1);
  }

  return july1stCurrentYear;
};

// Get the start date of the current fortnight (fixed 14-day periods, always starting on Monday)
const getCurrentFortnightStart = (): Date => {
  // Use a single Date instance for all calculations to avoid race conditions
  const now = new Date();
  const today = getCurrentDateInTimezone(now);
  const referenceDate = getReferenceDate(now);

  // Calculate days since July 1st using timezone-aware date arithmetic
  // Convert both dates to the same timezone for accurate comparison
  const todayInTz = formatInTimeZone(today, timezone, "yyyy-MM-dd");
  const refInTz = formatInTimeZone(referenceDate, timezone, "yyyy-MM-dd");

  const todayDate = new Date(todayInTz + "T00:00:00Z");
  const refDate = new Date(refInTz + "T00:00:00Z");

  const daysSinceReference = Math.floor(
    (todayDate.getTime() - refDate.getTime()) / (1000 * 60 * 60 * 24),
  );

  // Calculate which fortnight we're in (0-based)
  const fortnightNumber = Math.floor(daysSinceReference / 14);

  // Calculate the start of this fortnight (fixed 14-day periods from July 1st)
  const fortnightStart = addDays(referenceDate, fortnightNumber * 14);

  // Ensure it starts on Monday using workspace timezone
  const mondayStart = startOfWeek(fortnightStart, { weekStartsOn: 1 });

  // Check if today falls within this fortnight
  const fortnightEnd = addDays(mondayStart, 13);

  // Reuse the same 'today' for comparison (already calculated from 'now')
  if (today >= mondayStart && today <= fortnightEnd) {
    return mondayStart;
  } else {
    return addDays(mondayStart, 14);
  }
};

/**
 * Checks whether a selected date interval matches the current fortnight.
 *
 * @param {Interval<Date>} fortnightRange - Selected fortnight interval.
 * @returns {boolean} True when the selected range is the current fortnight.
 */
export const isCurrentFortnight = (fortnightRange: Interval<Date>): boolean => {
  const startDate = new Date(fortnightRange.start);
  const endDate = new Date(fortnightRange.end);

  if (!isValidFortnightDateRange(startDate, endDate)) {
    return false;
  }

  const currentFortnight = getCurrentFortnight();
  const currentFortnightStart = new Date(currentFortnight.start);
  const currentFortnightEnd = new Date(currentFortnight.end);

  return (
    startDate.getTime() === currentFortnightStart.getTime() &&
    endDate.getTime() === currentFortnightEnd.getTime()
  );
};

/**
 * Gets the current workspace fortnight range.
 *
 * @returns {Interval<Date>} Current fortnight interval.
 */
export const getCurrentFortnight = (): Interval<Date> => {
  const start = getCurrentFortnightStart();

  const end = addDays(start, 13); // 14 days total (0-13)

  return {
    start,
    end,
  };
};

const isValidFortnightDateRange = (startDate: Date, endDate: Date): boolean => {
  const rangeDays = differenceInCalendarDays(endDate, startDate);

  return (
    !Number.isNaN(startDate.getTime()) &&
    !Number.isNaN(endDate.getTime()) &&
    rangeDays >= 0 &&
    rangeDays <= MAX_FORTNIGHT_RANGE_DAYS
  );
};

// Get fortnight from URL parameters if available, otherwise session storage, otherwise current fortnight
const getInitialFortnight = (): { firstDay: Date; lastDay: Date } => {
  // First, try to get from URL parameters (startDate and endDate)
  const urlParams = new URLSearchParams(window.location.search);
  const startDateParam = urlParams.get("startDate");
  const endDateParam = urlParams.get("endDate");

  if (startDateParam && endDateParam) {
    const startDate = parseBrowserDateQueryValue(startDateParam);
    const endDate = parseBrowserDateQueryValue(endDateParam);

    if (startDate && endDate && isValidFortnightDateRange(startDate, endDate)) {
      return {
        firstDay: startDate,
        lastDay: endDate,
      };
    }
  }

  // Fallback: try to get legacy fortnight parameter format
  const fortnightValues = urlParams.getAll("fortnight");
  if (fortnightValues.length >= 2) {
    // Parse the URL dates and convert them to workspace timezone
    const startDateStr = fortnightValues[0];
    const endDateStr = fortnightValues[1];

    const startDate = parseBrowserDateQueryValue(startDateStr);
    const endDate = parseBrowserDateQueryValue(endDateStr);

    if (startDate && endDate && isValidFortnightDateRange(startDate, endDate)) {
      return {
        firstDay: startDate,
        lastDay: endDate,
      };
    }
  }

  // Fallback to session storage if URL parameters are not available
  const sessionFortnightQuery = getFortnightFromSession();

  if (sessionFortnightQuery) {
    // Parse the session storage query string
    const cleanQuery = sessionFortnightQuery.startsWith("?")
      ? sessionFortnightQuery.slice(1)
      : sessionFortnightQuery;
    const params = new URLSearchParams(cleanQuery);

    const startDateStr = params.get("startDate");
    const endDateStr = params.get("endDate");

    if (startDateStr && endDateStr) {
      // Parse session dates and convert to workspace timezone
      const decodedStartDate = decodeURIComponent(startDateStr);
      const decodedEndDate = decodeURIComponent(endDateStr);

      const startDate = fromZonedTime(decodedStartDate + "T00:00:00", timezone);
      const endDate = fromZonedTime(decodedEndDate + "T00:00:00", timezone);

      // Validate that the dates are valid
      if (isValidFortnightDateRange(startDate, endDate)) {
        return {
          firstDay: startDate,
          lastDay: endDate,
        };
      }
    }
  }

  // Fallback to current fortnight if neither URL parameters nor session storage are available
  const currentFortnightStart = getCurrentFortnightStart();
  const currentFortnightEnd = addDays(currentFortnightStart, 13);

  return {
    firstDay: currentFortnightStart,
    lastDay: currentFortnightEnd,
  };
};

const FortnightPicker = forwardRef(
  ({ setDateRange }: FortnightPickerProps, ref) => {
    // Initialize state with fortnight from URL parameters, session storage, or current fortnight
    const [week, setWeek] = useState(() => getInitialFortnight());
    const [searchParams] = useSearchParams();
    const visibleFortnightQuery =
      sanitizeBrowserQueryParams(searchParams).toString();
    const setDateRangeRef = useRef(setDateRange);

    const handleDateChange = (dir: boolean): void => {
      // Move forward or backward by 14 days
      const daysToAdd = dir ? 14 : -14;
      const newFirstDay = startOfWeek(addDays(week.firstDay, daysToAdd), {
        weekStartsOn: 1,
      });
      // Last day is always 13 days after first day (14 days total: Monday to Sunday)
      const newLastDay = addDays(newFirstDay, 13);

      setWeek({
        firstDay: newFirstDay,
        lastDay: newLastDay,
      });
    };

    useImperativeHandle(ref, () => ({
      // upon clicking the clear filter this should reset the fortnight picker date too
      reset: (): void => {
        const currentFortnightStart = getCurrentFortnightStart();
        setWeek({
          // Reset to current fortnight based on July 1st reference
          firstDay: currentFortnightStart,
          lastDay: addDays(currentFortnightStart, 13), // 14 days total (0-13)
        });
      },
    }));

    useEffect(() => {
      setDateRangeRef.current = setDateRange;
    }, [setDateRange]);

    useEffect(() => {
      const sanitizedParams = sanitizeBrowserQueryParams(searchParams);
      const fortnightValues = sanitizedParams.getAll("fortnight");

      if (fortnightValues.length < 2) {
        return;
      }

      const startDate = parseBrowserDateQueryValue(fortnightValues[0]);
      const endDate = parseBrowserDateQueryValue(fortnightValues[1]);

      if (
        !startDate ||
        !endDate ||
        !isValidFortnightDateRange(startDate, endDate)
      ) {
        return;
      }

      setWeek((previousWeek) => {
        if (
          previousWeek.firstDay.getTime() === startDate.getTime() &&
          previousWeek.lastDay.getTime() === endDate.getTime()
        ) {
          return previousWeek;
        }

        return {
          firstDay: startDate,
          lastDay: endDate,
        };
      });
    }, [searchParams, visibleFortnightQuery]);

    useEffect(() => {
      setDateRangeRef.current(
        eachDayOfInterval({
          start: week.firstDay,
          end: week?.lastDay,
        }),
      );
    }, [week]);

    const formatDateForDisplay = (
      date: Date,
      isEndDate: boolean = false,
    ): string => {
      try {
        // Format the date directly in the workspace timezone without any device timezone manipulation
        // This ensures the display is always consistent with the workspace timezone
        const day = formatInTimeZone(date, timezone, "dd");
        const month = formatInTimeZone(date, timezone, "MMM");
        const year = formatInTimeZone(date, timezone, "yyyy");

        return `${day} ${month} ${year}`;
      } catch (error) {
        // Fallback: try to format using the date as-is in workspace timezone
        try {
          return formatInTimeZone(date, timezone, "dd MMM yyyy");
        } catch (fallbackError) {
          // Last resort: use basic date formatting (this should rarely be needed)
          const localDate = new Date(date);
          if (isEndDate) {
            localDate.setHours(0, 0, 0, 0);
          }

          const day = localDate.getDate().toString().padStart(2, "0");
          const month = localDate.toLocaleDateString("en-US", {
            month: "short",
          });
          const year = localDate.getFullYear();

          return `${day} ${month} ${year}`;
        }
      }
    };

    return (
      <div className='flex items-center justify-between h-10 gap-1 px-1 border rounded-md dashed-border min-w-fit'>
        <div className='arrow-container'>
          <ChevronLeft onClick={() => handleDateChange(false)} size={20} />
        </div>

        <p className='text-sm text-wrap'>
          {formatDateForDisplay(week.firstDay)} -{" "}
          {formatDateForDisplay(week.lastDay, true)}
        </p>
        <div className='arrow-container'>
          <ChevronRight onClick={() => handleDateChange(true)} size={20} />
        </div>
      </div>
    );
  },
);

FortnightPicker.displayName = "FortnightPicker";

export default FortnightPicker;
